<script>
import { shippingMethodProps, useShippingMethod } from "./mixin";
import Icon from "@/components/atoms/icon";
import Card from "@/components/molecules/card";
import Button from "@/components/atoms/button";
import Divider from "@/components/atoms/divider";

import yper2 from "@/assets/images/livraison/yper-2.png";
import delipop from "@/assets/images/livraison/delipop.png";

const LazyImage = defineAsyncComponent(() =>
  import("@/components/atoms/lazy-image")
);
const Popin = defineAsyncComponent(() =>
  import("@/components/molecules/popin")
);

export default {
  name: "ShippingMethodMobile",
  components: { Icon, Button, Card, Popin, Divider, LazyImage },
  props: {
    ...shippingMethodProps()
  },
  emits: ["click", "showOrderDetail", "showProduitsIncompatibles"],
  setup(props) {
    const { iconName, description, hasFraisLiquide, isLC, isDelipop } =
      useShippingMethod(props);

    return {
      iconName,
      description,
      hasFraisLiquide,
      isLC,
      isDelipop,
      yper2,
      delipop
    };
  }
};
</script>

<template>
  <Card
    class="c-shipping-method-mobile"
    :class="[
      disabled && 'c-shipping-method-mobile--disabled',
      selected && 'c-shipping-method-mobile--selected',
      infos && !isLC && !isDelipop && 'c-shipping-method-mobile--infos',
      isLadActif && 'c-shipping-method-mobile--abolad'
    ]"
    :elevation="disabled ? '' : 'sm'"
    radius="md"
  >
    <Button
      class="c-shipping-method-mobile__selector"
      :disabled="disabled"
      :aria-expanded="selected ? 'true' : 'false'"
      :aria-current="selected ? 'true' : 'false'"
      @click="$emit('click')"
    >
      <div class="c-shipping-method-mobile__content">
        <Icon
          class="c-shipping-method-mobile__icon"
          :name="iconName"
        />
        <div class="c-shipping-method__texts">
          <p class="c-shipping-method-mobile__label">
            {{ modeCommande.label_long }}
          </p>
          <div
            v-if="typePanier === 'ldd' && lddDelaiLinkLabel"
            class="c-shipping-method-mobile__time"
          >
            <Button
              link
              @click="$emit('showOrderDetail')"
            >
              {{ lddDelaiLinkLabel }}
            </Button>
          </div>
          <template v-else-if="infos">
            <p class="c-shipping-method-mobile__time">
              {{ infos.planning_mini_txt }}
            </p>
            <p
              v-if="isLadActif"
              class="c-shipping-method__cost u-font-19 u-font-500 u-my-xs"
              :class="disabled ? '' : 'u-color-green-2'"
            >
              {{ infos.tarif_mini_txt }}
            </p>
            <p
              v-else
              class="c-shipping-method-mobile__cost"
            >
              {{ infos.tarif_mini_txt }}
            </p>
          </template>
        </div>
      </div>
      <Icon
        class="c-shipping-method-mobile__icon-arrow"
        name="arrow-right-thin"
      />
    </Button>
    <template v-if="infos">
      <template v-if="infos.frais_inclus || infos.libelle_frais_inclus">
        <div
          v-if="infos.frais_inclus"
          class="c-shipping-method-mobile__infos"
        >
          <p
            v-for="(frais, index) in infos.frais_inclus"
            :key="`frais-${index}`"
            class="u-color-primary"
          >
            dont {{ lowercaseFirstLetter(frais.libelle) }} de
            {{ formatPrix(frais.montant) }} €
          </p>
        </div>

        <div
          v-if="infos.libelle_frais_inclus"
          class="u-text-center u-mb-md"
        >
          <Button
            type="button"
            link
            @click="$refs.popinInfo.show()"
          >
            En savoir plus sur le supplément liquide
          </Button>
        </div>
      </template>
      <p
        v-else-if="isLadActif && !disabled"
        class="u-font-14 u-font-500 u-px-sm u-pb-sm u-ml-lg25"
        :class="disabled ? '' : 'u-color-services'"
      >
        Profitez de votre abonnement à la Livraison
      </p>
      <Popin
        ref="popinInfo"
        aria-label="Frais de livraison"
        :slide-up="true"
        class="c-popin--info-shipping"
      >
        <p
          v-for="(frais, index) in infos.frais_inclus"
          :key="`frais-popin--${index}`"
          class="c-popin__subtitle u-color-primary u-mb-xs"
        >
          {{ uppercaseFirstLetter(frais.libelle) }} de
          {{ formatPrix(frais.montant) }} €
        </p>
        <p class="c-popin__text u-mb-md">
          {{ infos.libelle_frais_inclus }}
        </p>
        <Button
          v-if="hasFraisLiquide"
          link
          :to="`/panier?type_panier=${typePanier}`"
          class="u-mb-md"
        >
          Modifier les quantités de liquides de ma commande
        </Button>
      </Popin>
    </template>
    <template v-if="disabled">
      <div class="u-text-center u-mb-md">
        <Button
          type="button"
          link
          class="u-color-grey-1 u-text-underline"
          @click="$refs.popinFrais.show()"
        >
          Pourquoi ce mode n'est pas disponible ?
        </Button>
      </div>
      <Popin
        ref="popinFrais"
        aria-label="Raisons de l'impossibilité"
        slide-up
      >
        <template v-if="raisonsImpossible">
          <div
            v-for="(raison, index) in raisonsImpossible"
            :key="`raison-${index}-${raison.type_raison}`"
          >
            <p>
              {{ raison.raison }}
            </p>
            <Button
              v-if="raison.type_raison === 'produit_non_compatible'"
              class="u-mt-xs"
              link
              @click="$emit('showProduitsIncompatibles')"
            >
              Voir les produits incompatibles
            </Button>
            <Button
              v-if="raison.type_raison === 'frais_bloquant'"
              link
              to="/panier"
              class="u-mt-xs"
            >
              Voir les boissons de ma commande
            </Button>
            <Divider
              v-if="index < raisonsImpossible.length - 1"
              dotted
              class="u-my-sm"
            />
          </div>
        </template>
      </Popin>

      <p
        v-if="isLadActif && montantManquantLad > 0"
        class="u-font-16 u-px-sm u-pb-sm u-mt-xs"
      >
        Il manque {{ formatPrix(montantManquantLad) }} € à votre commande pour
        profiter de votre abonnement à la livraison
      </p>
    </template>

    <template v-if="infos && (isLC || isDelipop)">
      <div
        :class="disabled && 'c-shipping-method-mobile__collab--disabled'"
        class="c-shipping-method-mobile__collab"
      >
        <Card
          :class="isLC ? 'c-shipping-method-mobile__collab-encart' : 'c-shipping-method-mobile__delipop-encart'"
          border-color="border"
          radius="md"
        >
          <div class="u-flex u-flex-align-center u-flex-justify-center">
            <div>
              <p>
                Avec notre partenaire
              </p>
            </div>

            <div class="u-flex u-flex-align-center u-flex-shrink-none u-ml-xs">
              <LazyImage
                :src="isLC ? yper2 : delipop"
                width="88"
                height="33"
                alt=""
              />
            </div>
          </div>
        </Card>
      </div>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/shipping-method-mobile.scss";
</style>
